import React from 'react';
import type { PresaleDetails } from '@components/Song/MintComponents/MintComponent/useMintState';
import { FormatStrictCoundown } from '@components/basics/FormatStrictCountdown';
import { useAuth } from '@context/auth/base';
import { useMintComponent } from '../MintComponentContext';
import { useIsSaleInMoreThanADay } from '../useIsSaleInMoreThanADay';

type PanelSubInfoProps = {
  presaleDetails: PresaleDetails;
  startTimestamp: number;
  hasOngoingSale: boolean;
  hasPublicSaleStarted: boolean;
  canMintPublicSale: boolean;
  publicSaleSoldOut: boolean;
};

//TODO: Add tests : https://linear.app/sound/issue/ENG-7510/add-test-to-panel-subinfo-helpers
export const MintComponentPanelSubInfo = () => {
  const { presaleDetails, startTimestamp, publicSaleDetails, hasOnGoingSale } = useMintComponent();
  const { isAuthenticatedWithAPI } = useAuth();

  const hasPublicSaleStarted = publicSaleDetails.saleStarted;
  const canMintPublicSale = publicSaleDetails.canMintPublicSale;
  const publicSaleSoldOut = publicSaleDetails.isSoldOut;

  const isSaleInMoreThanADay = useIsSaleInMoreThanADay({ timeStamp: startTimestamp });

  const heading = getMintComponentHeading({
    isAuthenticatedWithAPI,
    isSaleInMoreThanADay,
    hasOngoingSale: hasOnGoingSale,
    hasPublicSaleStarted,
    canMintPublicSale,
    publicSaleSoldOut,
    presaleDetails,
    startTimestamp,
  });

  const subInfo = getMintComponentSubInfo({
    isAuthenticatedWithAPI,
    hasPresaleAccess: !!presaleDetails?.hasAccess,
    hasPresaleStarted: !!presaleDetails?.saleStarted,
    canMintPresale: presaleDetails ? !presaleDetails.maxedOutPerWallet : false,
    hasPublicSaleStarted,
    isSaleInMoreThanADay,
  });

  if (!heading) return null;

  return (
    <div className="flex flex-col items-center gap-0.5 pb-1 text-center font-base text-base-s text-white">
      <div className="inline-flex font-semibold">
        {heading.message}&nbsp;
        {heading.showTimer && (
          <FormatStrictCoundown timeStamp={startTimestamp} expandedFormat hideUnit />
        )}
      </div>
      {!!subInfo && (
        <div className="inline-flex font-normal">
          {subInfo.message}&nbsp;
          {subInfo.showTimer && (
            <FormatStrictCoundown timeStamp={startTimestamp} expandedFormat hideUnit />
          )}
        </div>
      )}
    </div>
  );
};

export const getMintComponentSubInfo = ({
  hasPresaleAccess,
  canMintPresale,
  hasPresaleStarted,
  hasPublicSaleStarted,
  isAuthenticatedWithAPI,
  isSaleInMoreThanADay,
}: {
  hasPresaleAccess: boolean;
  canMintPresale: boolean;
  hasPresaleStarted: boolean;
  hasPublicSaleStarted: boolean;
  isAuthenticatedWithAPI: boolean;
  isSaleInMoreThanADay: boolean;
}) => {
  if (!isAuthenticatedWithAPI) {
    return null;
  }

  if (hasPresaleAccess && hasPresaleStarted && !canMintPresale && !hasPublicSaleStarted) {
    return {
      message: isSaleInMoreThanADay ? 'Public sale starts on' : 'Public sale starts in',
      showTimer: true,
    };
  }

  return null;
};

export const getMintComponentHeading = ({
  presaleDetails,
  hasOngoingSale,
  hasPublicSaleStarted,
  isAuthenticatedWithAPI,
  canMintPublicSale,
  publicSaleSoldOut,
  isSaleInMoreThanADay,
}: {
  isAuthenticatedWithAPI: boolean;
  isSaleInMoreThanADay: boolean;
} & PanelSubInfoProps) => {
  //public sale started
  if (hasPublicSaleStarted) {
    //cap hit return info
    if (!canMintPublicSale && !publicSaleSoldOut && isAuthenticatedWithAPI) {
      return {
        message: "You've hit the cap",
      };
    }
    return null;
  }

  const publicSaleMessage = isSaleInMoreThanADay
    ? 'Public Sale starts on'
    : 'Public Sale starts in';
  const presaleMessage = isSaleInMoreThanADay ? 'Presale starts on' : 'Presale starts in';

  //no sale has started
  if (!hasOngoingSale && !presaleDetails?.saleStarted && !hasPublicSaleStarted) {
    /**
     * always show public start time if user is not authenticated
     */
    if (!isAuthenticatedWithAPI) {
      return {
        message: publicSaleMessage,
        showTimer: true,
      };
    }

    if (!!presaleDetails) {
      //no access or presale sold out show public sale
      if (!presaleDetails.hasAccess || presaleDetails.isSoldOut) {
        return {
          message: publicSaleMessage,
          showTimer: true,
        };
      }
      //has access and not sold out show presale
      return {
        message: presaleMessage,
        showTimer: true,
      };
    }
    return {
      message: publicSaleMessage,
      showTimer: true,
    };
  }

  //sold out presale
  if (
    !!presaleDetails &&
    presaleDetails.hasAccess &&
    presaleDetails.isSoldOut &&
    !hasPublicSaleStarted
  ) {
    return { message: 'Presale sold out', showTimer: false };
  }

  // Handling for presale situations
  if (!!presaleDetails && !presaleDetails.saleEnded) {
    if (presaleDetails.hasAccess) {
      return !presaleDetails.maxedOutPerWallet
        ? null
        : { message: "You've hit the cap for presale", showTimer: false };
    } else {
      return publicSaleSoldOut
        ? null
        : {
            message: publicSaleMessage,
            showTimer: true,
          };
    }
  }

  if (!canMintPublicSale && hasPublicSaleStarted && !publicSaleSoldOut && isAuthenticatedWithAPI) {
    return { message: "You've hit the cap", showTimer: false };
  }

  if (!hasPublicSaleStarted) {
    return {
      message: publicSaleMessage,
      showTimer: true,
    };
  }

  return null;
};
